/** @file Dialog to add a single unit */
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';

import type { PipeDiameter, PipeMaterial } from '../types';

type Props = {
  name: string;
  pipeDiameter: PipeDiameter;
  pipeMaterial: PipeMaterial;
  open: boolean;
  onClose: () => void;
  addOrEdit: 'ADD' | 'EDIT';
  type: 'UNIT' | 'COMMON_AREA';
  onSubmit: (data: {
    name: string;
    pipeDiameter: PipeDiameter;
    pipeMaterial: PipeMaterial;
    unitFlag?: ('NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT')[];
  }) => void;
  loading: boolean;
};

/**
 * Create Unit dialog
 *
 * @param {object} props - Component properties
 * @returns {React.Component} - Create Unit dialog
 */
export const FormDialog = (props: Props) => {
  const [localName, setLocalName] = React.useState(props.name);
  const [unitFlag, setUnitFlag] = React.useState<'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT'>(
    'NON_BILLABLE',
  );
  // Bill model when adding a regular unit (type === 'UNIT'). Default 'BY_USAGE'
  // matches today's behavior (the dialog used to send no unit_flags at all for
  // units). 'NON_BILLABLE_UNIT' is the new option that surfaces a unit which
  // keeps tenants/meters but isn't invoiced; typically subsidized housing.
  const [unitBillModel, setUnitBillModel] = React.useState<'BY_USAGE' | 'NON_BILLABLE_UNIT'>(
    'BY_USAGE',
  );
  const [openSplitWarning, setOpenSplitWarning] = React.useState(false);
  // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to `Unit.name`
  // in customer-lambdas/doc/webapp.yaml — DB column is NOT NULL but accepts empty
  // strings, so without this client-side guard a user can save a blank unit name.
  const isNameBlank = (localName ?? '').trim() === '';

  // The dialog stays mounted across opens (it's rendered persistently from
  // the side drawer; closing just toggles `open`). useState only initializes
  // on first mount, so without this effect the previous session's name and
  // bill model would still be in the form when the user reopens it. Reset
  // every time the dialog flips from closed to open.
  React.useEffect(() => {
    if (props.open) {
      setLocalName(props.name);
      setUnitFlag('NON_BILLABLE');
      setUnitBillModel('BY_USAGE');
      setOpenSplitWarning(false);
    }
  }, [props.open, props.name]);

  const buildBody = () => {
    const trimmedName = (localName ?? '').trim();
    const body: {
      name: string;
      pipeDiameter: PipeDiameter;
      pipeMaterial: PipeMaterial;
      unitFlag?: ('NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT')[];
    } = {
      name: trimmedName,
      pipeDiameter: props.pipeDiameter,
      pipeMaterial: props.pipeMaterial,
    };
    if (props.type === 'COMMON_AREA') {
      body.unitFlag = ['COMMON_AREA', unitFlag];
    } else if (unitBillModel === 'NON_BILLABLE_UNIT') {
      // Regular unit, but excluded from billing. NON_BILLABLE alone (no
      // COMMON_AREA flag) is the DB shape that distinguishes a non-billable
      // residential unit from a non-billable common area.
      body.unitFlag = ['NON_BILLABLE'];
    }
    return body;
  };

  const handleSubmit = () => {
    if (isNameBlank) return;
    const body = buildBody();

    if (props.type === 'COMMON_AREA' && unitFlag === 'SPLIT') {
      setOpenSplitWarning(true);
    } else {
      props.onSubmit(body);
    }
  };

  const handleConfirmSplit = () => {
    setOpenSplitWarning(false);
    if (isNameBlank) return;
    props.onSubmit(buildBody());
  };

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xs">
        <DialogTitle>
          {`${props.addOrEdit === 'ADD' ? 'Add' : 'Edit'} ${
            props.type === 'UNIT' ? 'Unit' : 'Common Area'
          }`}
          <IconButton
            aria-label="close"
            onClick={props.onClose}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {props.name !== undefined && (
              <TextField
                label={'Name'}
                placeholder={'Name'}
                error={isNameBlank}
                helperText={
                  isNameBlank
                    ? 'Name is required'
                    : `eg: "1", "205-b"  (${(localName ?? '').length}/64)`
                }
                required={true}
                value={localName}
                sx={{ marginTop: 2 }}
                // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64`
                // to `Unit.name` in customer-lambdas/doc/webapp.yaml (mirroring units.name
                // varchar(64) in db_setup.sql), then delete this client-side cap + counter.
                // Today the DB write fails with a cryptic string_data_right_truncation
                // error if the cap is bypassed. The bulk-import paths
                // (Units/FormDialog/bulk.tsx, Structures/Create/CreateUnits/index.tsx)
                // also bypass this client-side cap, so the backend fix is the durable one.
                inputProps={{ maxLength: 64 }}
                onChange={(e) => {
                  setLocalName(e.target.value);
                }}
              />
            )}
            {props.type === 'COMMON_AREA' && (
              <FormControl fullWidth>
                <InputLabel id="unitflag">Bill Model</InputLabel>
                <Select
                  labelId="unitflag"
                  value={unitFlag}
                  label="Bill Model"
                  onChange={(e) =>
                    setUnitFlag(e.target.value as 'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT')
                  }
                >
                  <MenuItem value={'NON_BILLABLE'}>Non Billable</MenuItem>
                  <MenuItem value={'SPLIT'}>Split</MenuItem>
                </Select>
              </FormControl>
            )}
            {props.type === 'UNIT' && (
              <FormControl fullWidth>
                <InputLabel id="unit-bill-model">Bill Model</InputLabel>
                <Select
                  labelId="unit-bill-model"
                  value={unitBillModel}
                  label="Bill Model"
                  onChange={(e) =>
                    setUnitBillModel(e.target.value as 'BY_USAGE' | 'NON_BILLABLE_UNIT')
                  }
                >
                  <MenuItem value={'BY_USAGE'}>By Usage</MenuItem>
                  <MenuItem value={'NON_BILLABLE_UNIT'}>Non Billable</MenuItem>
                </Select>
              </FormControl>
            )}
            {props.type === 'UNIT' && unitBillModel === 'NON_BILLABLE_UNIT' && (
              <Alert severity="info">
                This unit will not be invoiced for water usage. Tenants and meters stay attached,
                and you can switch back to By Usage at any time.
              </Alert>
            )}
            {props.type === 'COMMON_AREA' && unitFlag === 'NON_BILLABLE' && (
              <Alert severity="info">
                This common area will not be invoiced. Usage is still recorded for tracking and leak
                detection.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined onClick={props.onClose}>Cancel</SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleSubmit} loading={props.loading} disabled={isNameBlank}>
            {props.addOrEdit === 'ADD' ? 'Add' : 'Save'}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSplitWarning}
        onClose={() => setOpenSplitWarning(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                color: 'warning.main',
                // ~12% tint of warning color so the icon stands out without
                // shouting; the theme doesn't define warning.lighter so we
                // build the tint directly from warning.main.
                backgroundColor: (t) => `${t.palette.warning.main}1f`,
                flexShrink: 0,
              }}
            >
              <WarningAmberIcon />
            </Box>
            <Typography component="span" variant="h6" fontWeight={700}>
              Split Billing
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setOpenSplitWarning(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2">
            This is <strong>not recommended for residential properties</strong> and can be
            prohibited by law. Please confirm this is a commercial property where you'd like to
            split the common water costs among tenants.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined onClick={() => setOpenSplitWarning(false)}>
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton color="warning" onClick={handleConfirmSplit}>
            I understand, continue
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
